<template>
  <tr
    v-if="bluredOrNotHidden"
    :class="{ active: isActive, hovered: showActions }"
    class="draggableBlock"
    data-block="Carousel"
    tabindex="0"
    @mouseover="mouseOver"
    @mouseleave="mouseOut"
    @click.stop="() => click()"
  >
    <td
      :class="{
        'block-borders': showDragBorder,
        'dragged-opacity': isCurrentElementDragged,
      }"
      style="word-break: break-word; font-size: 0px"
    >
      <blockActions
        v-if="showActions"
        :column-padding-x="columnPaddingX"
        :column-width="columnWidth"
        :disable-up-movement="disableUpMovement"
        :disable-down-movement="disableDownMovement"
        :block-height="blockHeight"
        :block-width="blockWidth"
        :is-active="isActive"
        :is-last-column="isLastColumn"
        :section-index="sectionIndex"
        :column-index="columnIndex"
        :block-index="blockIndex"
        :show-paste="showPaste"
        :copied="copied"
        @duplicate="copyBlock"
        @remove="deleteBlock"
        @moveUp="moveUp"
        @moveDown="moveDown"
        @copy="copyToClipboard"
      ></blockActions>

      <div class="mj-carousel" :style="{ padding: padding }">
        <div
          class="mj-carousel-content"
          style="
            display: table;
            width: 100%;
            table-layout: fixed;
            text-align: center;
            font-size: 0px;
          "
        >
          <div v-if="thumbnailsVisible">
            <div
              v-for="(child, i) in children"
              :key="i"
              style="
                border: 2px solid transparent;
                display: inline-block;
                overflow: hidden;
              "
              :class="{
                'mj-carousel-thumbnail': child.attributes.src,
                'mj-carousel-thumbnail-selected':
                  selectedImageIndex === i && child.attributes.src !== '',
              }"
            >
              <div
                v-if="!child.attributes.src"
                :style="{ width: thumbnailWidth }"
                class="placeholder-container"
                @click="clickImage(i as number)"
              >
                <div class="img-text-container">
                  <svg
                    class="img-1"
                    width="64"
                    height="34"
                    viewBox="0 0 60 30"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="14"
                      y="1"
                      width="32"
                      height="28"
                      rx="2"
                      stroke="#6DA8FF"
                      stroke-width="2"
                    />
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M22.5 12C24.433 12 26 10.433 26 8.5C26 6.567 24.433 5 22.5 5C20.567 5 19 6.567 19 8.5C19 10.433 20.567 12 22.5 12ZM20 24H40C40.5523 24 41 23.5523 41 23V20L35.0701 13.531C34.3037 12.695 32.9962 12.6628 32.1897 13.4602L26.3333 19.25L24.1385 17.3046C23.3217 16.5806 22.0752 16.6452 21.3376 17.4499L19 20V23C19 23.5523 19.4477 24 20 24Z"
                      fill="#6DA8FF"
                    />
                    <path
                      d="M7.65685 8L1.29289 14.364C0.902369 14.7545 0.902369 15.3877 1.29289 15.7782L7.65685 22.1421"
                      stroke="#6DA8FF"
                      stroke-width="2"
                      stroke-linecap="round"
                    />
                    <path
                      d="M52.071 7.92893L58.435 14.2929C58.8255 14.6834 58.8255 15.3166 58.435 15.7071L52.071 22.0711"
                      stroke="#6DA8FF"
                      stroke-width="2"
                      stroke-linecap="round"
                    />
                  </svg>

                  <svg
                    class="img-2"
                    width="64"
                    height="34"
                    viewBox="0 0 64 34"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="15"
                      y="2"
                      width="34"
                      height="30"
                      rx="3"
                      fill="#6DA8FF"
                    />
                    <rect
                      x="14"
                      y="1"
                      width="36"
                      height="32"
                      rx="4"
                      stroke="white"
                      stroke-opacity="0.4"
                      stroke-width="2"
                    />
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M24.5 14C26.433 14 28 12.433 28 10.5C28 8.567 26.433 7 24.5 7C22.567 7 21 8.567 21 10.5C21 12.433 22.567 14 24.5 14ZM22 26H42C42.5523 26 43 25.5523 43 25V22L37.0701 15.531C36.3037 14.695 34.9962 14.6628 34.1897 15.4602L28.3333 21.25L26.1385 19.3046C25.3217 18.5806 24.0752 18.6452 23.3376 19.4499L21 22V25C21 25.5523 21.4477 26 22 26Z"
                      fill="white"
                    />
                    <path
                      d="M9.65685 10L3.29289 16.364C2.90237 16.7545 2.90237 17.3877 3.29289 17.7782L9.65685 24.1421"
                      stroke="white"
                      stroke-opacity="0.4"
                      stroke-width="6"
                      stroke-linecap="round"
                    />
                    <path
                      d="M54.071 9.92893L60.435 16.2929C60.8255 16.6834 60.8255 17.3166 60.435 17.7071L54.071 24.0711"
                      stroke="white"
                      stroke-opacity="0.4"
                      stroke-width="6"
                      stroke-linecap="round"
                    />
                    <path
                      d="M9.65685 10L3.29289 16.364C2.90237 16.7545 2.90237 17.3877 3.29289 17.7782L9.65685 24.1421"
                      stroke="#6DA8FF"
                      stroke-width="2"
                      stroke-linecap="round"
                    />
                    <path
                      d="M54.071 9.92893L60.435 16.2929C60.8255 16.6834 60.8255 17.3166 60.435 17.7071L54.071 24.0711"
                      stroke="#6DA8FF"
                      stroke-width="2"
                      stroke-linecap="round"
                    />
                  </svg>

                  <div class="img-text">Carousel Image</div>
                </div>
              </div>
              <img
                v-else
                style="display: block; height: auto; width: 100%"
                :style="{
                  width: thumbnailWidth,
                  borderRadius: thumbnailBorderRadius,
                }"
                :src="child.attributes.src"
                :width="thumbnailWidth.replace('px', '')"
                @mouseover="chooseImage(i as number)"
              />
            </div>
          </div>
          <table
            style="
              caption-side: top;
              display: table-caption;
              table-layout: fixed;
              width: 100%;
            "
            border="0"
            cell-padding="0"
            cell-spacing="0"
            width="100%"
            role="presentation"
            class="mj-carousel-main"
          >
            <tbody>
              <tr>
                <td
                  class="mj-carousel-icons-cell"
                  style="font-size: 0px; padding: 0px"
                >
                  <div class="mj-carousel-previous-icons">
                    <img
                      :src="leftIcon"
                      alt="previous"
                      style="display: block; height: auto"
                      :style="{ width: moveIconsWidth }"
                      :width="moveIconsWidth.replace('px', '')"
                      @click="movePreviousImage"
                    />
                  </div>
                </td>
                <td style="padding: 0px">
                  <div class="mj-carousel-images">
                    <div class="mj-carousel-image">
                      <img
                        v-if="selectedImg"
                        :src="selectedImg"
                        style="
                          border-radius: 6px;
                          display: block;
                          width: 600px;
                          max-width: 100%;
                          height: auto;
                        "
                        :style="{
                          borderRadius: borderRadius,
                        }"
                        width="600"
                        border="0"
                      />

                      <div
                        v-else
                        :style="{ width: mainWidth + 'px' }"
                        class="placeholder-container"
                        @click="clickImage(0)"
                      >
                        <div class="img-text-container">
                          <svg
                            class="img-1"
                            width="64"
                            height="34"
                            viewBox="0 0 60 30"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <rect
                              x="14"
                              y="1"
                              width="32"
                              height="28"
                              rx="2"
                              stroke="#6DA8FF"
                              stroke-width="2"
                            />
                            <path
                              fill-rule="evenodd"
                              clip-rule="evenodd"
                              d="M22.5 12C24.433 12 26 10.433 26 8.5C26 6.567 24.433 5 22.5 5C20.567 5 19 6.567 19 8.5C19 10.433 20.567 12 22.5 12ZM20 24H40C40.5523 24 41 23.5523 41 23V20L35.0701 13.531C34.3037 12.695 32.9962 12.6628 32.1897 13.4602L26.3333 19.25L24.1385 17.3046C23.3217 16.5806 22.0752 16.6452 21.3376 17.4499L19 20V23C19 23.5523 19.4477 24 20 24Z"
                              fill="#6DA8FF"
                            />
                            <path
                              d="M7.65685 8L1.29289 14.364C0.902369 14.7545 0.902369 15.3877 1.29289 15.7782L7.65685 22.1421"
                              stroke="#6DA8FF"
                              stroke-width="2"
                              stroke-linecap="round"
                            />
                            <path
                              d="M52.071 7.92893L58.435 14.2929C58.8255 14.6834 58.8255 15.3166 58.435 15.7071L52.071 22.0711"
                              stroke="#6DA8FF"
                              stroke-width="2"
                              stroke-linecap="round"
                            />
                          </svg>

                          <svg
                            class="img-2"
                            width="64"
                            height="34"
                            viewBox="0 0 64 34"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <rect
                              x="15"
                              y="2"
                              width="34"
                              height="30"
                              rx="3"
                              fill="#6DA8FF"
                            />
                            <rect
                              x="14"
                              y="1"
                              width="36"
                              height="32"
                              rx="4"
                              stroke="white"
                              stroke-opacity="0.4"
                              stroke-width="2"
                            />
                            <path
                              fill-rule="evenodd"
                              clip-rule="evenodd"
                              d="M24.5 14C26.433 14 28 12.433 28 10.5C28 8.567 26.433 7 24.5 7C22.567 7 21 8.567 21 10.5C21 12.433 22.567 14 24.5 14ZM22 26H42C42.5523 26 43 25.5523 43 25V22L37.0701 15.531C36.3037 14.695 34.9962 14.6628 34.1897 15.4602L28.3333 21.25L26.1385 19.3046C25.3217 18.5806 24.0752 18.6452 23.3376 19.4499L21 22V25C21 25.5523 21.4477 26 22 26Z"
                              fill="white"
                            />
                            <path
                              d="M9.65685 10L3.29289 16.364C2.90237 16.7545 2.90237 17.3877 3.29289 17.7782L9.65685 24.1421"
                              stroke="white"
                              stroke-opacity="0.4"
                              stroke-width="6"
                              stroke-linecap="round"
                            />
                            <path
                              d="M54.071 9.92893L60.435 16.2929C60.8255 16.6834 60.8255 17.3166 60.435 17.7071L54.071 24.0711"
                              stroke="white"
                              stroke-opacity="0.4"
                              stroke-width="6"
                              stroke-linecap="round"
                            />
                            <path
                              d="M9.65685 10L3.29289 16.364C2.90237 16.7545 2.90237 17.3877 3.29289 17.7782L9.65685 24.1421"
                              stroke="#6DA8FF"
                              stroke-width="2"
                              stroke-linecap="round"
                            />
                            <path
                              d="M54.071 9.92893L60.435 16.2929C60.8255 16.6834 60.8255 17.3166 60.435 17.7071L54.071 24.0711"
                              stroke="#6DA8FF"
                              stroke-width="2"
                              stroke-linecap="round"
                            />
                          </svg>

                          <div class="img-text">Carousel Image</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
                <td
                  class="mj-carousel-icons-cell"
                  style="font-size: 0px; padding: 0px"
                >
                  <div class="mj-carousel-next-icons">
                    <img
                      :src="rightIcon"
                      alt="next"
                      style="display: block; height: auto"
                      :style="{ width: moveIconsWidth }"
                      :width="moveIconsWidth.replace('px', '')"
                      @click="moveNextImage"
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </td>
  </tr>
</template>

<script lang="ts" setup>
  import blockProperties from "@/composables/useBlockProperties";
  import blockActions from "./blockActions.vue";
  import { ref, computed, watchEffect } from "vue";
  import { useMjmlStore } from "@/stores/mjmlStore";
  import { TopolCarouslBlock } from "@/schemas/template/blocks/carouselBlockSchema";

  const props = defineProps<{
    block: TopolCarouslBlock;
    columnIndex: number;
    sectionIndex: number;
    blockIndex: number;
    isLocked: boolean;
  }>();

  const {
    showActions,
    showPaste,
    blockHeight,
    blockWidth,
    bluredOrNotHidden,
    isActive,
    disableUpMovement,
    disableDownMovement,
    columnPaddingX,
    columnWidth,
    isCurrentElementDragged,
    showDragBorder,
    isLastColumn,
    copied,
    mouseOver,
    mouseOut,
    click,
    copyBlock,
    deleteBlock,
    moveUp,
    moveDown,
    copyToClipboard,
  } = blockProperties(props);

  const mjmlStore = useMjmlStore();

  const images = ref([]);

  watchEffect(() => {
    images.value = props.block.children.reduce((acc: any, child: any) => {
      acc.push(child.attributes.src);
      return acc;
    }, []);
  });

  const selectedImageIndex = ref(0);

  const selectedImg = computed(() => {
    return images.value[selectedImageIndex.value];
  });

  const chooseImage = (index: number) => {
    selectedImageIndex.value = index;
  };

  const moveNextImage = () => {
    if (selectedImageIndex.value + 1 >= images.value.length) {
      selectedImageIndex.value = 0;
    } else {
      selectedImageIndex.value++;
    }
  };

  const movePreviousImage = () => {
    if (selectedImageIndex.value - 1 < 0) {
      selectedImageIndex.value = images.value.length - 1;
    } else {
      selectedImageIndex.value--;
    }
  };

  const clickImage = (index: number) => {
    setTimeout(() => {
      const chooseButton: HTMLButtonElement | null = document.querySelector(
        `[data-testid="carousel-item-${index}"] [data-testid="section-choose-image"]`
      );
      if (chooseButton) {
        chooseButton.click();
      }
    }, 100);
  };

  const MOBILE_BODY_WIDTH = 340;

  const mainWidth = computed<number>(() => {
    const section = mjmlStore.getSelectedSectionByIndex(props.sectionIndex);
    const templateWidth = mjmlStore.isMobileView
      ? Math.min(mjmlStore.getBodyWidth, MOBILE_BODY_WIDTH)
      : mjmlStore.getBodyWidth;

    if (section.children.length !== 0) {
      const px = getPaddingValue("left") + getPaddingValue("right");
      return (
        templateWidth - Number(moveIconsWidth.value.replace("px", "")) * 2 - px
      );
    }

    return templateWidth;
  });

  const children = computed<any>(() => {
    return props.block.children;
  });

  const moveIconsWidth = computed(() => {
    return props.block.attributes["icon-width"];
  });

  const borderRadius = computed(() => {
    return props.block.attributes["border-radius"];
  });

  const thumbnailWidth = computed(() => {
    return props.block.attributes["tb-width"];
  });

  const thumbnailsVisible = computed(() => {
    return props.block.attributes["thumbnails"] !== "hidden";
  });

  const thumbnailBorderRadius = computed(() => {
    return props.block.attributes["tb-border-radius"];
  });

  const thumbnailHoverBorderColor = computed(() => {
    return props.block.attributes["tb-hover-border-color"];
  });

  const iconWidth = computed(() => {
    return props.block.attributes["icon-width"];
  });

  const leftIcon = computed(() => {
    return props.block.attributes["left-icon"];
  });

  const rightIcon = computed(() => {
    return props.block.attributes["right-icon"];
  });

  const thumbNailSelectedBorderColor = computed(() => {
    return props.block.attributes["tb-selected-border-color"];
  });

  const padding = computed(() => {
    return props.block.attributes["padding"];
  });

  /**
   * Returns padding value for given direction, default direction is top
   */
  function getPaddingValue(direction?: string) {
    if (padding.value) {
      const paddingSplit = padding.value.split(" ");

      //padding: 20px 40px;
      if (paddingSplit.length === 2) {
        return parseInt(paddingSplit[getShortDirectionNumber(direction)]);
      }
      //padding: 12px 30px 12px 30px;
      else {
        return parseInt(paddingSplit[getDirectionNumber(direction)]);
      }
    }
    return 0;
  }

  /**
   * Converts direction name to corresponding array index in standard padding array
   */
  function getDirectionNumber(direction?: string) {
    if (direction === "top") {
      return 0;
    } else if (direction === "right") {
      return 1;
    } else if (direction === "bottom" || direction === "bot") {
      return 2;
    } else if (direction === "left") {
      return 3;
    } else {
      return 0;
    }
  }

  /**
   * Converts direction name to corresponding array index in short padding array
   */
  function getShortDirectionNumber(direction?: string) {
    if (direction === "top" || direction === "bottom" || direction === "bot") {
      return 0;
    } else if (direction === "right" || direction === "left") {
      return 1;
    } else {
      return 0;
    }
  }
</script>

<style scoped lang="scss">
  .mj-carousel {
    -webkit-user-select: none;
    -moz-user-select: none;
    user-select: none;
  }

  .mj-carousel-icons-cell {
    display: table-cell !important;
    width: v-bind(iconWidth) !important;
  }

  .mj-carousel-next,
  .mj-carousel-previous {
    display: none !important;
  }

  .mj-carousel-thumbnail,
  .mj-carousel-next,
  .mj-carousel-previous {
    touch-action: manipulation;
  }

  .mj-carousel-previous-icons,
  .mj-carousel-next-icons,
  .mj-carousel-thumbnail:hover {
    border-color: v-bind(thumbnailHoverBorderColor) !important;
  }

  .mj-carousel-thumbnail-selected {
    border-color: v-bind(thumbNailSelectedBorderColor) !important;
  }

  .placeholder-container {
    color: rgb(109, 168, 255);
    background-color: rgb(231, 243, 255);
    text-decoration: none;
    text-align: center;
    line-height: 18px;
    display: flex;
    position: relative;
    justify-content: center;
    cursor: pointer;
    // border-radius: 6px;
    border: none;
    outline: rgba(109, 168, 255, 0.3) 1px solid;

    max-width: calc(100%);
    height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;

    font: var(
      --chip-font,
      var(--chip-font-weight, var(--font-normal))
        var(--chip-font-size, var(--font-xs)) / var(--chip-font-line-height, 1)
        var(--chip-font-family, var(--font-family-primary))
    );

    .img-text-container {
      margin-top: 12px;
      pointer-events: none;

      .img-1 {
        padding-bottom: 12px;
        display: inline-block;
      }

      .img-2 {
        padding-bottom: 12px;
        display: none;
      }
    }
  }

  .placeholder-container:hover,
  .placeholder-container-hovered {
    background-color: rgb(206, 231, 255) !important;
    color: rgb(72, 131, 255) !important;
    border-radius: 0px;
    .img-text-container {
      .img-1 {
        padding-bottom: 12px;
        display: none;
      }

      .img-2 {
        padding-bottom: 12px;
        display: inline-block;
      }
    }
  }
</style>
